import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { LayoutDashboard } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fold } from "@/shared/lib/fold";
import { useCan, useTaxReturns } from "@/shared/lib/session";
import { PageHeader } from "@/shared/ui/Page";
import { SearchBox } from "@/shared/ui/SearchBox";
import { MAP, useAreas, type AreaKey } from "./areas";

/** One report: the page and tab it's on, what it's about, its name, and what it answers (reportsHub.hint.<id>). */
interface Report { id: string; area: AreaKey; to: string; tab?: string; label: string; perm?: string; tax?: "vat" | "wht" }

/** Every report, under what it's about, the most used first. */
export const REPORTS: Report[] = [
  { id: "aging", area: "sales", to: "/reports/summary", tab: "aging", label: "reports.aging", perm: "invoicing.report.read" },
  { id: "sales", area: "sales", to: "/reports/summary", tab: "sales", label: "reports.sales", perm: "invoicing.report.read" },
  { id: "pipeline", area: "sales", to: "/reports/summary", tab: "crm", label: "reports.pipeline", perm: "crm.deal.read" },
  { id: "forecast", area: "sales", to: "/crm/reports", tab: "forecast", label: "reportsHub.name.forecast" },
  { id: "salespeople", area: "sales", to: "/crm/reports", tab: "team", label: "reportsHub.name.salespeople" },
  { id: "salesCards", area: "sales", to: "/crm/reports", tab: "cards", label: "salesReport.cards" },
  { id: "valuation", area: "stock", to: "/inventory/reports", tab: "valuation", label: "invrep.tabs.valuation" },
  { id: "movement", area: "stock", to: "/inventory/reports", tab: "movement", label: "invrep.tabs.movement" },
  { id: "stockCard", area: "stock", to: "/inventory/reports", tab: "card", label: "invrep.tabs.card" },
  { id: "expiring", area: "stock", to: "/inventory/reports", tab: "expiring", label: "invrep.tabs.expiring" },
  { id: "slow", area: "stock", to: "/inventory/reports", tab: "slow", label: "reportsHub.name.slow" },
  { id: "pl", area: "money", to: "/accounting/reports", tab: "pl", label: "acc.pl" },
  { id: "bs", area: "money", to: "/accounting/reports", tab: "bs", label: "acc.bs" },
  { id: "cashflow", area: "money", to: "/accounting/reports", tab: "cashflow", label: "acc.cashflow" },
  { id: "monthly", area: "money", to: "/accounting/reports", tab: "monthly", label: "acc.monthly" },
  { id: "tb", area: "money", to: "/accounting/reports", tab: "tb", label: "acc.tb" },
  { id: "gl", area: "money", to: "/accounting/reports", tab: "gl", label: "acc.gl" },
  { id: "partners", area: "money", to: "/accounting/reports", tab: "partners", label: "acc.partners" },
  { id: "expenses", area: "money", to: "/reports/summary", tab: "expenses", label: "reports.expenses", perm: "invoicing.expense.read" },
  { id: "vatReturn", area: "money", to: "/taxes", tab: "vat", label: "vat.tab", tax: "vat" },
  { id: "wht", area: "money", to: "/taxes", tab: "wht", label: "wht.label", tax: "wht" },
  { id: "vatSummary", area: "money", to: "/accounting/reports", tab: "vat", label: "acc.vat" },
  { id: "workforce", area: "team", to: "/hr/reports", tab: "workforce", label: "hr.workforce" },
  { id: "attendance", area: "team", to: "/hr/reports", tab: "attendance", label: "hr.attendanceSummary" },
  { id: "leave", area: "team", to: "/hr/reports", tab: "leave", label: "hr.leaveLiability" },
  { id: "localization", area: "team", to: "/hr/reports", tab: "localization", label: "reportsHub.name.localization" },
  { id: "kpi", area: "team", to: "/hr/performance", label: "kpi.title" },
  { id: "time", area: "work", to: "/reports/summary", tab: "time", label: "reports.time", perm: "projects.time.read" },
];

/**
 * The reports the user may open: those of the pages they may open, less those that need a
 * permission of their own the user lacks (one page, several apps' reports) or a tax return
 * the company's country doesn't have.
 */
export function hubReports(open: Set<string>, can: (perm: string) => boolean, taxes: { vat: boolean; wht: boolean }): Report[] {
  return REPORTS.filter((r) => open.has(r.to) && (!r.perm || can(r.perm)) && (!r.tax || taxes[r.tax]));
}

/** "All reports": every report the user may open, named and said what it answers, under what it's about, found by typing. */
export function ReportsHub() {
  const { t } = useTranslation();
  const areas = useAreas();
  const can = useCan();
  const taxes = useTaxReturns();
  const [q, setQ] = useState("");
  const open = new Set(areas.flatMap((a) => a.pages.map((p) => p.to.split("?")[0])));
  const needle = fold(q.trim());
  const reports = hubReports(open, can, taxes)
    .map((r) => ({ ...r, name: t(r.label), hint: t(`reportsHub.hint.${r.id}`) }))
    .filter((r) => !needle || fold(`${r.name} ${r.hint} ${t(`areas.${r.area}`)}`).includes(needle));
  const groups = MAP.map((a) => ({ key: a.key, items: reports.filter((r) => r.area === a.key) })).filter((g) => g.items.length > 0);
  return (
    <>
      <PageHeader title={t("nav.allReports")} />
      {can("core.report.read") && (
        <Link to="/reports/custom" className="panel mb-7 flex items-center gap-3 p-4 transition-colors hover:border-ledger">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-ledger-soft text-ledger"><LayoutDashboard className="size-[18px]" aria-hidden /></span>
          <span className="min-w-0">
            <span className="block font-semibold">{t("rep.title")}</span>
            <span className="block text-[13px] leading-snug text-ink-3">{t("rep.hubHint")}</span>
          </span>
        </Link>
      )}
      <div className="mb-7 max-w-md"><SearchBox value={q} onChange={setQ} /></div>
      {groups.length === 0 && <p className="py-8 text-ink-3">{t("common.noResults")}</p>}
      <div className="space-y-9">
        {groups.map((g) => (
          <section key={g.key} aria-labelledby={`reports-${g.key}`}>
            <h2 id={`reports-${g.key}`} className="mb-1 text-[13px] font-semibold text-ink-3">{t(`areas.${g.key}`)}</h2>
            <ul className="grid gap-x-10 md:grid-cols-2">
              {g.items.map((r) => (
                <li key={r.id} className="border-b border-line-2">
                  <Link to={r.to} search={r.tab ? { tab: r.tab } : {}} className="group block py-3.5">
                    <span className="block font-semibold group-hover:text-ledger">{r.name}</span>
                    <span className="mt-0.5 block text-[13px] leading-snug text-ink-3">{r.hint}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
}
